import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { cn } from "@/lib/utils";
import { api } from "./api";

/* ── Count chip builder ──────────────────────────────────── */
function buildCountOptions(max) {
  const bases = [10, 15, 20, 25, 30];
  const opts   = bases.filter((n) => n < max);
  opts.push(max); // always include the max as "All N"
  return [...new Set(opts)];
}

/* ── Stat row ────────────────────────────────────────────── */
function BucketRow({ emoji, label, subLabel, count, weight, result, color }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-card/70 px-4 py-3 shadow-soft">
      <span className="text-2xl leading-none">{emoji}</span>
      <div className="min-w-0 flex-1">
        <p className={cn("font-display text-sm font-black", color)}>{label}</p>
        <p className="text-[10px] font-semibold text-muted-foreground">{subLabel}</p>
      </div>
      <div className="flex items-center gap-2 text-right text-xs font-extrabold text-muted-foreground">
        <span className="tabular-nums text-foreground">{count}</span>
        <span>×</span>
        <span className="tabular-nums">{weight}</span>
        <span>=</span>
        <span className={cn("tabular-nums font-black", color)}>{result}</span>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────── */
export default function RevisionLanding() {
  const { childId } = useParams();
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();
  const helpEnabled  = searchParams.get("help") === "true";

  const [info,    setInfo]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [count,   setCount]   = useState(null); // selected question count

  useEffect(() => {
    api(`/revision/info?child_id=${childId}`)
      .then((d) => {
        setInfo(d);
        // Default to middle chip or 20 if available
        const opts = buildCountOptions(Math.min(d.max_questions, 50));
        const def  = opts.includes(20) ? 20 : opts[Math.floor(opts.length / 2)];
        setCount(def);
      })
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [childId]);

  const handleStart = () => {
    if (!count) return;
    navigate(`/vocab/child/${childId}/quiz/mixed?revision=true&count=${count}${helpEnabled ? "&help=true" : ""}`);
  };

  const maxQ    = info ? Math.min(info.max_questions, 50) : 0;
  const opts    = info ? buildCountOptions(maxQ) : [];

  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-gradient-to-b from-[#FFF7EF] via-[#FFF1E6] to-[#DCF3EC]">
      {/* Decorative blobs */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute right-[12%] top-[6%] h-20 w-20 rounded-full bg-teal/30 blur-xl" />
        <div className="absolute left-[8%] top-[40%] h-28 w-28 rounded-full bg-secondary/20 blur-2xl" />
      </div>

      {/* Back button */}
      <div className="relative z-10 px-4 py-3 sm:px-8">
        <button
          onClick={() => navigate(`/vocab/child/${childId}`)}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-card/85 text-foreground shadow-soft backdrop-blur transition hover:bg-card"
          aria-label="Back"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto flex max-w-lg flex-col items-center px-6 pb-16 pt-2 text-center">

        {/* Mascot */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Mascot size={80} mood="encouraging" animated={false} />
        </motion.div>

        <h1 className="mt-4 font-display text-3xl font-black tracking-tight text-foreground sm:text-4xl">
          Ready to revise? 🔁
        </h1>
        <p className="mt-2 text-sm font-semibold text-muted-foreground">
          We quiz the words you find tricky more often — so they stick!
        </p>

        {/* Loading */}
        {loading && (
          <div className="mt-8 w-full space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-2xl bg-muted/60" />
            ))}
          </div>
        )}

        {/* Not enough words */}
        {!loading && info && info.words_seen < 20 && (
          <div className="mt-8 rounded-3xl border border-border bg-card p-6 shadow-soft">
            <p className="text-2xl">📚</p>
            <p className="mt-2 font-display text-base font-black text-foreground">
              Not quite ready yet
            </p>
            <p className="mt-1 text-sm font-semibold text-muted-foreground">
              Learn at least 20 words first, then come back to revise.
              You've seen {info.words_seen} so far — keep going!
            </p>
            <BloomButton
              className="mt-4"
              onClick={() => navigate(`/vocab/child/${childId}/learn?all_words=true`)}
            >
              Go learn some words →
            </BloomButton>
          </div>
        )}

        {/* Breakdown + picker */}
        {!loading && info && info.words_seen >= 20 && (
          <>
            {/* Bucket breakdown */}
            <div className="mt-7 w-full space-y-2.5">
              <BucketRow
                emoji="🔴"
                label="Tricky words"
                subLabel="You find these tough — we'll practise them lots!"
                count={info.struggling_count}
                weight={3}
                result={info.struggling_count * 3}
                color="text-destructive"
              />
              <BucketRow
                emoji="📖"
                label="Still learning"
                subLabel="Getting there — a bit more practice needed"
                count={info.learning_count}
                weight={2}
                result={info.learning_count * 2}
                color="text-primary-strong"
              />
              <BucketRow
                emoji="🟢"
                label="You know well"
                subLabel="Keeping these sharp so you don't forget!"
                count={info.confident_count}
                weight={1}
                result={info.confident_count * 1}
                color="text-teal-ink"
              />

              {/* Total */}
              <div className="flex items-center justify-between rounded-2xl bg-muted/60 px-4 py-3">
                <span className="text-sm font-extrabold text-foreground">
                  Up to {maxQ} questions available
                </span>
                {maxQ >= 50 && (
                  <span className="text-[10px] font-bold text-muted-foreground">
                    (capped at 50 per session)
                  </span>
                )}
              </div>
            </div>

            {/* Count picker */}
            <div className="mt-6 w-full">
              <p className="mb-3 text-sm font-extrabold text-foreground">
                How many questions today?
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {opts.map((n) => {
                  const isMax   = n === maxQ;
                  const isCapped = info.max_questions > 50 && isMax;
                  const label   = isMax ? (isCapped ? `Max (${n})` : `All ${n}`) : `${n}`;
                  return (
                    <button
                      key={n}
                      onClick={() => setCount(n)}
                      className={cn(
                        "rounded-full px-4 py-2 font-display text-sm font-extrabold transition-all",
                        count === n
                          ? "bg-teal text-white shadow-soft scale-105"
                          : "bg-card/80 text-foreground shadow-soft hover:bg-card hover:scale-105",
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 w-full"
            >
              <BloomButton
                size="lg"
                fullWidth
                rightIcon={ArrowRight}
                onClick={handleStart}
                disabled={!count}
              >
                Let's go! — {count} questions
              </BloomButton>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}